import { Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { forwardRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

/**
 * Componente que muestra un Snackbar personalizado con valores por defecto.
 * @param {Object} props - Propiedades del Snackbar.
 * @param {boolean} props.open - Estado del Snackbar.
 * @param {function} props.closeSnackbar - Función para cerrar el Snackbar.
 * @param {number} props.duration - Duración en milisegundos antes de que el Snackbar se cierre automáticamente.
 * @param {string} props.severity - Severidad del Alert (success, error, warning, info).
 * @param {string} props.message - Mensaje que se mostrará en el Snackbar.
 * @param {string} props.vertical - Posición vertical del Snackbar (top, bottom).
 * @param {string} props.horizontal - Posición horizontal del Snackbar (left, center, right).
 */
const SnackbarAlert = ({
  open = false,
  closeSnackbar = () => {},
  duration = 6000,
  severity = 'info',
  message = 'This is a message',
  vertical = 'top',
  horizontal = 'right'
}) => {
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    closeSnackbar();
  };

  SnackbarAlert.propTypes = {
    open: PropTypes.bool,
    closeSnackbar: PropTypes.func.isRequired,
    duration: PropTypes.number,
    severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
    message: PropTypes.string,
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right'])
  };

  const closeSnackbarMemoized = useCallback(() => {
    closeSnackbar();
  }, [closeSnackbar]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        closeSnackbarMemoized();
      }, duration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [open, duration, closeSnackbarMemoized]);

  return (
    open && (
      <Snackbar open={open} onClose={handleClose} autoHideDuration={duration} anchorOrigin={{ vertical, horizontal }}>
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    )
  );
};

export default SnackbarAlert;
